import {
  ApplicationConfig,
  ErrorHandler,
  inject,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { registerLocaleData } from '@angular/common';
import localeDeIt from '@angular/common/locales/de-IT';
import localeEnGb from '@angular/common/locales/en-GB';
import localeIt from '@angular/common/locales/it';
import {
  provideRouter,
  withComponentInputBinding,
  withRouterConfig,
  TitleStrategy,
} from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideTransloco } from '@jsverse/transloco';
import { provideTranslocoLocale } from '@jsverse/transloco-locale';
import { routes } from './app.routes';
import { AppTitleStrategy } from './core/app-title.strategy';
import { AuthService } from './services/auth.service';
import { AppVersionService } from './core/services/app-version.service';
import { jwtInterceptor } from './core/interceptors/jwt.interceptor';
import { loadingInterceptor } from './core/loading/loading.interceptor';
import { httpErrorInterceptor } from './core/http-error.interceptor';
import { GlobalErrorHandler } from './core/global-error-handler';
import { LocalizationService } from './core/services/localization.service';
import { TranslocoHttpLoader } from './core/transloco-http-loader';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';

registerLocaleData(localeEnGb);
registerLocaleData(localeDeIt);
registerLocaleData(localeIt);

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(
      routes,
      withComponentInputBinding(),
      withRouterConfig({ paramsInheritanceStrategy: 'always', canceledNavigationResolution: 'computed' }),
    ),
    { provide: TitleStrategy, useClass: AppTitleStrategy },
    provideHttpClient(withInterceptors([jwtInterceptor, loadingInterceptor, httpErrorInterceptor])),
    provideAnimationsAsync(),
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    provideTransloco({
      config: {
        availableLangs: ['en', 'de', 'it'],
        defaultLang: 'en',
        fallbackLang: 'en',
        missingHandler: { logMissingKey: true },
        reRenderOnLangChange: true,
      },
      loader: TranslocoHttpLoader,
    }),
    provideTranslocoLocale({
      langToLocaleMapping: { en: 'en-GB', de: 'de-IT', it: 'it-IT' },
    }),
    provideAppInitializer(() => {
      inject(LocalizationService);
    }),
    provideAppInitializer(() => {
      const authService = inject(AuthService);
      return authService.checkAuthStatus();
    }),
    provideAppInitializer(() => {
      inject(AppVersionService).start();
    }),
  ],
};
